// ============================================
// VFXLightInjectPass — inject VFX particle lights into the light buffer
// ============================================
//
// For each VFX system with `emitsLight`, dispatches a compute shader that
// reads the particle storage buffer and writes GPULight entries into the
// main light buffer (after CPU-submitted lights).
//
// Extends LightingComputePass for consistent lifecycle management.
//
// Usage:
// ```ts
// const injectPass = new VFXLightInjectPass(ctx);
//
// // Per frame, after LightManager.end() and VFX update:
// const vfxLightCount = injectPass.inject(lightManager, lightEmittingSystems);
//
// // Then cull with combined count:
// // lightManager patches visibleLightCount += vfxLightCount before cull
// ```

import { LightingComputePass } from './LightingComputePass';
import type { IRenderContext } from '../core/IRenderContext';
import type { BufferHandle, ComputePipelineHandle, ShaderHandle } from '../types';
import { NULL_HANDLE } from '../types';
import { defineStruct, GPUType } from '../core/GPUTypes';
import { GPU_LIGHT_SIZE } from './LightLayouts';
import type { LightManager } from './LightManager';

import { getCompiledShader } from '../pipeline/ShaderCache';

// ============================================================================
// InjectUniforms struct (must match vfx-light-inject.wgsl)
// ============================================================================

const INJECT_UNIFORMS = defineStruct('InjectUniforms', {
  cpuLightCount:     GPUType.u32().build(),
  maxLights:         GPUType.u32().build(),
  maxParticles:      GPUType.u32().build(),
  radius:            GPUType.f32().build(),
  intensity:         GPUType.f32().build(),
  falloff:           GPUType.f32().build(),
  colorMode:         GPUType.u32().build(),
  intensityFromAlpha: GPUType.u32().build(),
  overrideColor:     GPUType.f32().vec4(),
  emitterX:          GPUType.f32().build(),
  emitterY:          GPUType.f32().build(),
  emitterScaleX:     GPUType.f32().build(),
  emitterScaleY:     GPUType.f32().build(),
});

// ============================================================================
// Types
// ============================================================================

/**
 * A light-emitting VFX system with the data needed for injection.
 */
export interface LightEmittingSystem {
  /** GPU particle storage buffer */
  particleBuffer: BufferHandle;
  /** Maximum particles in this system */
  maxParticles: number;
  /** Emitter world position X */
  emitterX: number;
  /** Emitter world position Y */
  emitterY: number;
  /** Emitter scale X */
  emitterScaleX: number;
  /** Emitter scale Y */
  emitterScaleY: number;
  /** Light configuration from the vfx.output.light node */
  lightConfig: {
    radius: number;
    intensity: number;
    falloff: number;
    colorMode: number;
    overrideColor: readonly number[];
    intensityFromAlpha: boolean;
  };
}

// ============================================================================
// VFXLightInjectPass
// ============================================================================

export class VFXLightInjectPass extends LightingComputePass {
  // ---- GPU resources ----
  private computeShader: ShaderHandle = NULL_HANDLE;
  private computePipeline: ComputePipelineHandle = NULL_HANDLE;
  private _uniformBuffer: BufferHandle = NULL_HANDLE;

  // ---- CPU staging ----
  private readonly uniformWriter = INJECT_UNIFORMS.createWriter();
  private readonly _uniformF32 = new Float32Array(INJECT_UNIFORMS.size / 4);

  // ---- Last injection result ----
  private _lastVFXLightCount = 0;

  constructor(ctx: IRenderContext) {
    super(ctx, 'VFXLightInject');
    
    if (this.supportsCompute) {
      this.initialize();
    } else {
      console.warn('[VFXLightInjectPass] Compute not supported — VFX light injection unavailable');
    }
  }

  // ========================================================================
  // LightingComputePass Implementation
  // ========================================================================

  protected override onInitialize(): void {
    // Create compute shader
    this.computeShader = this.createShader(getCompiledShader('lighting/vfx-light-inject'), 'vfx-light-inject');

    // Create compute pipeline
    // Bindings: uniform(0), storage(1)=particles, storage(2)=lights
    this.computePipeline = this.createPipeline({
      shader: this.computeShader,
      entryPoint: 'main',
      workgroupSize: [64, 1, 1],
      storageBufferCount: 2,
      label: 'vfx-light-inject-pipeline',
    });

    // Create uniform buffer
    this._uniformBuffer = this.createUniformBuffer(
      INJECT_UNIFORMS.size,
      'vfx-light-inject-uniforms',
    );
  }

  protected override onDispose(): void {
    this.destroyBuffer(this._uniformBuffer);
    this._uniformBuffer = NULL_HANDLE;

    this.destroyPipeline(this.computePipeline);
    this.computePipeline = NULL_HANDLE;

    this.destroyShader(this.computeShader);
    this.computeShader = NULL_HANDLE;
  }

  // ========================================================================
  // Public API
  // ========================================================================

  /** Number of VFX lights injected in the last frame. */
  get lastVFXLightCount(): number {
    return this._lastVFXLightCount;
  }

  /**
   * Inject particle lights into the main light buffer.
   *
   * Must be called after `LightManager.end()` and VFX `update()`, but
   * before `LightCullPass.cull()`.
   *
   * @param lightManager - LightManager with finalized CPU lights
   * @param systems - Light-emitting VFX systems to inject
   * @returns Estimated max VFX light count (sum of maxParticles; actual may be less)
   */
  inject(lightManager: LightManager, systems: readonly LightEmittingSystem[]): number {
    if (!this.isReady || systems.length === 0) {
      this._lastVFXLightCount = 0;
      return 0;
    }

    const ctx = this.ctx;
    const cpuLightCount = lightManager.visibleLightCount;
    const maxLights = lightManager.maxLights;

    // Dispatch one injection per light-emitting system.
    // Each system's particles map 1:1 to light buffer slots starting at
    // cpuLightCount + totalMaxVFXLights. Dead particles write null lights
    // so stale data from previous frames is always cleared.
    let totalMaxVFXLights = 0;

    for (const sys of systems) {
      const remaining = maxLights - cpuLightCount - totalMaxVFXLights;
      if (remaining <= 0) break;

      const effectiveMax = Math.min(sys.maxParticles, remaining);

      // Update uniforms for this system
      const oc = sys.lightConfig.overrideColor;
      const ocObj = !Array.isArray(oc) && oc && typeof oc === 'object' && 'r' in oc
        ? oc as unknown as { r: number; g: number; b: number; a?: number }
        : null;
      this.uniformWriter.setAll({
        cpuLightCount: cpuLightCount + totalMaxVFXLights,
        maxLights,
        maxParticles: effectiveMax,
        radius: sys.lightConfig.radius,
        intensity: sys.lightConfig.intensity,
        falloff: sys.lightConfig.falloff,
        colorMode: sys.lightConfig.colorMode,
        intensityFromAlpha: sys.lightConfig.intensityFromAlpha ? 1 : 0,
        overrideColor: Array.isArray(oc)
          ? [oc[0], oc[1], oc[2], oc[3]]
          : ocObj
            ? [ocObj.r, ocObj.g, ocObj.b, ocObj.a ?? 1]
            : [1, 1, 1, 1],
        emitterX: sys.emitterX,
        emitterY: sys.emitterY,
        emitterScaleX: sys.emitterScaleX || 1,
        emitterScaleY: sys.emitterScaleY || 1,
      });

      // Copy to pre-allocated F32 and upload
      const src = new Uint8Array(this.uniformWriter.buffer);
      const dst = new Uint8Array(this._uniformF32.buffer);
      dst.set(src);
      ctx.updateBuffer(this._uniformBuffer, this._uniformF32);

      // Dispatch compute
      ctx.setComputePipeline!(this.computePipeline);
      ctx.bindComputeResources!({
        uniforms: [this._uniformBuffer],
        storageBuffers: [
          { buffer: sys.particleBuffer },
          { buffer: lightManager.lightBuffer },
        ],
      });

      const workgroups = Math.ceil(effectiveMax / 64);
      ctx.dispatchCompute!({ x: workgroups });

      totalMaxVFXLights += effectiveMax;
    }

    // Flush compute so light data is visible to subsequent passes
    ctx.flushCompute!();

    this._lastVFXLightCount = totalMaxVFXLights;
    return this._lastVFXLightCount;
  }

  /** Release all GPU resources. */
  destroy(): void {
    this.dispose();
  }
}
